import {
  type Annotation,
  arrowParts,
  type Rect,
  textColumnWidth,
  textFont,
  wrapText,
} from './geometry';

/**
 * Draw annotations in view pixels. Caller sets the transform.
 * Arrows are a straight shaft plus a filled head, including while dragging.
 * Text wraps to its frozen column, or to the selection's right edge when that
 * column has not been committed yet. Every line is drawn; clipping is the
 * caller's choice.
 */
export function strokeAnnotations(
  ctx: CanvasRenderingContext2D,
  annotations: readonly Annotation[],
  selection: Rect | null,
): void {
  for (const item of annotations) {
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    switch (item.kind) {
      case 'rect':
        ctx.strokeStyle = item.color;
        ctx.lineWidth = item.stroke;
        ctx.strokeRect(item.x, item.y, item.width, item.height);
        break;
      case 'ellipse':
        drawEllipse(ctx, item);
        break;
      case 'arrow':
        drawArrow(ctx, item);
        break;
      case 'brush':
        drawBrush(ctx, item.points, item.color, item.stroke);
        break;
      case 'mosaic':
        drawMosaic(ctx, item);
        break;
      case 'text':
        drawText(ctx, item, selection);
        break;
      default: {
        const unreachable: never = item;
        throw new Error(`unknown annotation ${String(unreachable)}`);
      }
    }
    ctx.restore();
  }
}

function drawText(
  ctx: CanvasRenderingContext2D,
  item: Extract<Annotation, { kind: 'text' }>,
  selection: Rect | null,
): void {
  ctx.fillStyle = item.color;
  ctx.font = textFont(item.fontSize);
  ctx.textBaseline = 'top';
  const maxWidth = textColumnWidth(item, selection);
  if (!(maxWidth > 0)) {
    ctx.fillText(item.text, item.x, item.y);
    return;
  }
  const lines = wrapText(
    item.text,
    maxWidth,
    (text) => ctx.measureText(text).width,
  );
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    if (line) {
      ctx.fillText(line, item.x, item.y + index * item.fontSize);
    }
  }
}

function drawEllipse(
  ctx: CanvasRenderingContext2D,
  item: Extract<Annotation, { kind: 'ellipse' }>,
): void {
  const rx = item.width / 2;
  const ry = item.height / 2;
  if (!(rx > 0) || !(ry > 0)) {
    return;
  }
  ctx.strokeStyle = item.color;
  ctx.lineWidth = item.stroke;
  ctx.beginPath();
  ctx.ellipse(item.x + rx, item.y + ry, rx, ry, 0, 0, Math.PI * 2);
  ctx.stroke();
}

function drawMosaic(
  ctx: CanvasRenderingContext2D,
  item: Extract<Annotation, { kind: 'mosaic' }>,
): void {
  for (const tile of item.tiles) {
    ctx.fillStyle = tile.color;
    ctx.fillRect(
      tile.x - item.size / 2,
      tile.y - item.size / 2,
      item.size,
      item.size,
    );
  }
}

function drawBrush(
  ctx: CanvasRenderingContext2D,
  points: readonly { x: number; y: number }[],
  color: string,
  stroke: number,
): void {
  if (points.length === 0) {
    return;
  }
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = stroke;
  ctx.beginPath();
  const first = points[0];
  if (!first) {
    return;
  }
  ctx.moveTo(first.x, first.y);
  for (const point of points.slice(1)) {
    ctx.lineTo(point.x, point.y);
  }
  if (points.length === 1) {
    ctx.arc(first.x, first.y, stroke / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  ctx.stroke();
}

/** Straight shaft with a filled triangular head. The shaft stops inside the head. */
function drawArrow(
  ctx: CanvasRenderingContext2D,
  item: Extract<Annotation, { kind: 'arrow' }>,
): void {
  const dx = item.x2 - item.x1;
  const dy = item.y2 - item.y1;
  const length = Math.hypot(dx, dy);
  if (length < 1) {
    return;
  }
  const angle = Math.atan2(dy, dx);
  const head = arrowParts(item);
  if (!head) {
    return;
  }
  const headLength = Math.hypot(
    head.tip.x - head.left.x,
    head.tip.y - head.left.y,
  );
  const overlap = Math.min(item.stroke, headLength * 0.4);
  const shaft = headLength - overlap;
  ctx.strokeStyle = item.color;
  ctx.fillStyle = item.color;
  ctx.lineWidth = item.stroke;
  ctx.lineCap = 'butt';
  ctx.beginPath();
  ctx.moveTo(item.x1, item.y1);
  ctx.lineTo(
    item.x2 - shaft * Math.cos(angle),
    item.y2 - shaft * Math.sin(angle),
  );
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(head.tip.x, head.tip.y);
  ctx.lineTo(head.left.x, head.left.y);
  ctx.lineTo(head.right.x, head.right.y);
  ctx.closePath();
  ctx.fill();
}

/**
 * Crop the frozen frame to the selection and bake annotations into a PNG.
 * Strokes that fall outside the selection are clipped.
 */
export async function exportSelectionPng(input: {
  source: CanvasImageSource;
  imageWidth: number;
  imageHeight: number;
  viewWidth: number;
  viewHeight: number;
  selection: Rect;
  annotations: readonly Annotation[];
}): Promise<Uint8Array> {
  const scaleX = input.imageWidth / input.viewWidth;
  const scaleY = input.imageHeight / input.viewHeight;
  const width = Math.max(1, Math.round(input.selection.width * scaleX));
  const height = Math.max(1, Math.round(input.selection.height * scaleY));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('canvas is not available');
  }
  const sx = input.selection.x * scaleX;
  const sy = input.selection.y * scaleY;
  ctx.drawImage(input.source, sx, sy, width, height, 0, 0, width, height);
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, width, height);
  ctx.clip();
  ctx.translate(-sx, -sy);
  ctx.scale(scaleX, scaleY);
  strokeAnnotations(ctx, input.annotations, input.selection);
  ctx.restore();
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (!value || value.size === 0) {
        reject(new Error('selection export produced an empty image'));
        return;
      }
      resolve(value);
    }, 'image/png');
  });
  return new Uint8Array(await blob.arrayBuffer());
}
